import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NoteFieldWithContextMenu from './NoteFieldWithContextMenu';

export interface EditPasswordFormData {
  id: string;
  title: string;
  notes: string;
  category: string;
}

interface EditPasswordFormProps {
  initialData: EditPasswordFormData;
  onClose: () => void;
  onSave: (data: { id: string; title: string; content: string; category?: string | null }) => void | Promise<void>;
  /** Mevcut kayıtlardan türetilen kategoriler; combobox öneri listesinde gösterilir. */
  suggestedCategories?: string[];
  /** Şablon uygula listesinde "Şablon oluştur" gösterilir ve tıklanınca çağrılır (örn. şablon oluşturma modalını aç). */
  onCreateTemplate?: () => void;
  /** Şifre düzenleme modalında true ise not alanı önizleme modunda açılır ve "Sadece önizleme" butonu gösterilmez. */
  startInPreview?: boolean;
  /** Yeni şifre eklerken true: kaynak ve önizleme yan yana değil, Kaynak/Önizleme ile tek görünüm değiştirilebilir. */
  singleViewMode?: boolean;
  /** Düzenleme modunda sil butonu gösterilir; tıklanınca bu callback çağrılır (id ile). */
  onDelete?: (id: string) => void;
}

const EditPasswordForm: React.FC<EditPasswordFormProps> = ({ initialData, onClose, onSave, suggestedCategories = [], onCreateTemplate, startInPreview = false, singleViewMode = false, onDelete }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialData.title);
  const [notes, setNotes] = useState(initialData.notes);
  const [category, setCategory] = useState(initialData.category);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(-1);
  const categoryInputRef = useRef<HTMLInputElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  const filteredCategories = useMemo(() => {
    if (!category.trim()) return suggestedCategories;
    const search = category.toLowerCase().trim();
    return suggestedCategories.filter((cat) => cat.toLowerCase().includes(search));
  }, [category, suggestedCategories]);

  useEffect(() => {
    setCategory(initialData.category);
  }, [initialData.category]);

  const handleCategorySelect = (value: string) => {
    setCategory(value);
    setCategoryError(null);
    setShowCategoryDropdown(false);
    setSelectedCategoryIndex(-1);
    categoryInputRef.current?.blur();
  };

  const handleCategoryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showCategoryDropdown || filteredCategories.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedCategoryIndex((prev) =>
          prev < filteredCategories.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedCategoryIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedCategoryIndex >= 0 && selectedCategoryIndex < filteredCategories.length) {
          handleCategorySelect(filteredCategories[selectedCategoryIndex]);
        } else if (filteredCategories.length === 1) {
          handleCategorySelect(filteredCategories[0]);
        }
        break;
      case 'Escape':
        setShowCategoryDropdown(false);
        setSelectedCategoryIndex(-1);
        categoryInputRef.current?.blur();
        break;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedCategory = category.trim();
    const titleRequiredMsg = t('forms.description.required', 'Başlık zorunludur.');
    const categoryRequiredMsg = t('forms.category.required', 'Kategori zorunludur.');
    let hasError = false;
    if (!trimmedTitle) {
      setTitleError(titleRequiredMsg);
      hasError = true;
    } else {
      setTitleError(null);
    }
    if (!trimmedCategory) {
      setCategoryError(categoryRequiredMsg);
      hasError = true;
    } else {
      setCategoryError(null);
    }
    if (hasError) return;
    setIsSaving(true);
    try {
      await onSave({
        id: initialData.id,
        title: trimmedTitle,
        content: notes.trim(),
        category: trimmedCategory || null,
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      {/* Header: title ve category yan yana */}
      <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="edit-password-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('forms.description.label', 'Başlık')}
            </label>
            {titleError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">
                {titleError}
              </p>
            )}
            <input
              type="text"
              id="edit-password-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleError(null);
              }}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow"
              placeholder={t('forms.description.placeholder', 'Başlık girin')}
              autoFocus
            />
          </div>
          <div className="relative" ref={categoryDropdownRef}>
          <label htmlFor="edit-password-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('forms.category.label', 'Kategori')}
          </label>
          {categoryError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">
              {categoryError}
            </p>
          )}
          <input
            ref={categoryInputRef}
            type="text"
            id="edit-password-category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setCategoryError(null);
              setShowCategoryDropdown(true);
              setSelectedCategoryIndex(-1);
            }}
            onFocus={() => {
              setShowCategoryDropdown(true);
              setSelectedCategoryIndex(-1);
            }}
            onBlur={() => {
              setTimeout(() => {
                setShowCategoryDropdown(false);
                setSelectedCategoryIndex(-1);
              }, 200);
            }}
            onKeyDown={handleCategoryKeyDown}
            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow"
            placeholder={t('forms.category.placeholder', 'Kategori girin')}
            autoComplete="off"
          />
          {showCategoryDropdown && suggestedCategories.length > 0 && (
            <div
              className="absolute z-10 w-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg max-h-48 overflow-y-auto"
              role="listbox"
            >
              {filteredCategories.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('forms.category.noMatch', 'Eşleşen kategori yok')}
                </div>
              ) : (
                filteredCategories.map((cat, index) => (
                  <button
                    key={cat}
                    type="button"
                    role="option"
                    aria-selected={index === selectedCategoryIndex}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleCategorySelect(cat);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      index === selectedCategoryIndex
                        ? 'bg-primary/15 dark:bg-primary/25 text-primary'
                        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {cat}
                  </button>
                ))
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* İçerik: not alanı */}
      <div className="flex-1 overflow-hidden flex flex-col px-4 lg:px-6 py-3 lg:py-4 min-h-0">
        <label htmlFor="edit-password-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('forms.notes.label', 'Not')}
        </label>
        <NoteFieldWithContextMenu
          id="edit-password-notes"
          value={notes}
          onChange={setNotes}
          rows={22}
          wrapperClassName="flex-1 min-h-0"
          className="w-full flex-1 min-h-[480px] px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-shadow resize-y"
          placeholder={t('forms.notes.placeholder', 'Not ekleyin...')}
          onCreateTemplate={onCreateTemplate}
          showPreviewOnlyToggle={false}
          defaultView={startInPreview ? 'preview' : undefined}
          singleViewMode={singleViewMode}
        />
      </div>

      {/* Footer */}
      <div className="px-4 lg:px-6 py-3 lg:py-4 bg-gray-50/80 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700/80 flex justify-between items-center gap-3 flex-shrink-0">
        <div className="flex gap-3">
          {onDelete && initialData.id ? (
            <button
              type="button"
              onClick={() => onDelete(initialData.id)}
              className="px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors font-medium flex items-center gap-2"
            >
              <i className="fa-solid fa-trash" />
              {t('common.delete')}
            </button>
          ) : null}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!title.trim() || !category.trim() || isSaving}
            className="px-5 py-2.5 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity font-medium shadow-lg shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" />
                {t('common.loading', 'Yükleniyor...')}
              </>
            ) : (
              t('common.save')
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default EditPasswordForm;
